"use client";

import { useState, useEffect, useCallback } from 'react';
import Pagination from '../components/Pagination';
import {
    Plus,
    Edit,
    Trash2,
    Search,
    RefreshCw,
    X,
    Check,
    Filter,
    Download,
    Upload,
    ChevronLeft,
    ChevronRight,
    TrendingUp,
    Activity,
    Target,
    FileText,
    Utensils,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Info,
    Printer,
    AlertCircle,
    AlertTriangle
} from 'lucide-react';
import {
    AdminGetAllFoodMacros,
    AdminCreateFoodMacro,
    AdminUpdateFoodMacro,
    AdminDeleteFoodMacro,
    AdminBulkIngestFoodMacros,
    AdminExportFoodMacros,
    AdminSearchFoodMacros
} from '../components/Api';

export default function FoodMacrosPage() {
    const [macros, setMacros] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [selectedMacro, setSelectedMacro] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [pageSize, setPageSize] = useState(6);
    const [showInstructions, setShowInstructions] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: 'food', direction: 'asc' });
    const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });
    const [formErrors, setFormErrors] = useState({});
    const [formData, setFormData] = useState({
        food: '',
        qty: '',
        unit: 'g',
        caloriesInKcal: '',
        carbsInGrams: '',
        proteinInGrams: '',
        fatInGrams: '',
        remarks: ''
    });

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const fetchMacros = useCallback(async (page = currentPage, size = pageSize, sort = sortConfig, search = searchTerm) => {
        setLoading(true);
        try {
            // Note: Api.jsx currently doesn't pass sort, but we could update it
            const response = search ? await AdminSearchFoodMacros(search, page, size) : await AdminGetAllFoodMacros(page, size);
            const data = response.data;
            if (data && data.content) {
                let sortedData = [...data.content];
                if (sort.key) {
                    sortedData.sort((a, b) => {
                        const aVal = a[sort.key] || '';
                        const bVal = b[sort.key] || '';
                        if (aVal < bVal) return sort.direction === 'asc' ? -1 : 1;
                        if (aVal > bVal) return sort.direction === 'asc' ? 1 : -1;
                        return 0;
                    });
                }
                setMacros(sortedData);
                setTotalPages(data.totalPages);
                setTotalElements(data.totalElements);
                setCurrentPage(data.number);
            } else {
                setMacros(data || []);
            }
        } catch (error) {
            console.error('Error fetching food macros:', error);
            showToast('Error fetching food macros', 'error');
        } finally {
            setLoading(false);
        }
    }, [currentPage, pageSize, sortConfig, searchTerm]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchMacros();
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [fetchMacros]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const renderSortIcon = (key) => {
        if (sortConfig.key !== key) return <ArrowUpDown className="w-3 h-3 ml-1 opacity-30" />;
        return sortConfig.direction === 'asc' ?
            <ArrowUp className="w-3 h-3 ml-1 text-blue-600" /> :
            <ArrowDown className="w-3 h-3 ml-1 text-blue-600" />;
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.food || formData.food.trim() === '') errors.food = 'Food Name is required';
        if (!formData.qty || formData.qty.toString().trim() === '') errors.qty = 'Quantity is required';
        if (!formData.unit || formData.unit.trim() === '') errors.unit = 'Unit is required';

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleCreate = async () => {
        if (!validateForm()) return;
        try {
            await AdminCreateFoodMacro(formData);
            showToast('Food macro created successfully');
            setShowCreateDialog(false);
            resetForm();
            fetchMacros();
        } catch (error) {
            console.error('Error creating food macro:', error);
            showToast('Error creating food macro', 'error');
        }
    };

    const handleUpdate = async () => {
        if (!validateForm()) return;
        try {
            await AdminUpdateFoodMacro(selectedMacro.id, formData);
            showToast('Food macro updated successfully');
            setShowEditDialog(false);
            resetForm();
            fetchMacros(currentPage);
        } catch (error) {
            console.error('Error updating food macro:', error);
            showToast('Error updating food macro', 'error');
        }
    };

    const handleDelete = async (id) => {
        setConfirmModal({
            show: true,
            title: 'Delete Food Macro',
            message: 'Are you sure you want to delete this food macro? This action cannot be undone.',
            onConfirm: async () => {
                try {
                    await AdminDeleteFoodMacro(id);
                    showToast('Food macro deleted successfully');
                    fetchMacros();
                } catch (error) {
                    console.error('Error deleting food macro:', error);
                    showToast('Error deleting food macro', 'error');
                } finally {
                    setConfirmModal({ show: false, title: '', message: '', onConfirm: null });
                }
            }
        });
    };

    const handleBulkUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setLoading(true);
        try {
            await AdminBulkIngestFoodMacros(file);
            showToast('Bulk ingestion successful');
            fetchMacros();
        } catch (error) {
            console.error('Error bulk uploading:', error);
            showToast('Error in bulk ingestion', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleExport = async () => {
        try {
            const response = await AdminExportFoodMacros();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'food_macros.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            showToast('Export successful');
        } catch (error) {
            console.error('Error exporting food macros:', error);
            showToast('Error exporting food macros', 'error');
        }
    };

    const resetForm = () => {
        setFormErrors({});
        setFormData({
            food: '',
            qty: '',
            unit: 'g',
            caloriesInKcal: '',
            carbsInGrams: '',
            proteinInGrams: '',
            fatInGrams: '',
            remarks: ''
        });
        setSelectedMacro(null);
    };

    const openEditDialog = (item) => {
        setSelectedMacro(item);
        setFormData({
            food: item.food,
            qty: item.qty,
            unit: item.unit,
            caloriesInKcal: item.caloriesInKcal,
            carbsInGrams: item.carbsInGrams,
            proteinInGrams: item.proteinInGrams,
            fatInGrams: item.fatInGrams,
            remarks: item.remarks
        });
        setShowEditDialog(true);
    };

    const handleDownloadTemplate = () => {
        // Create headers
        const headers = [
            'food', 'qty', 'unit', 'caloriesInKcal', 'carbsInGrams',
            'proteinInGrams', 'fatInGrams', 'remarks'
        ];

        // Use Blob for CSV download
        const csvContent = headers.join(',') + '\n'
            + 'Chicken Breast,100,g,165,0,31,3.6,Boiled\n'
            + 'Brown Rice,100,g,111,23,2.6,0.9,Cooked';

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'food_macro_template.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            {confirmModal.show && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 backdrop-blur-sm transition-all duration-300">
                    <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl transform transition-all duration-300 scale-100 p-6 border border-gray-100">
                        <div className="flex flex-col items-center text-center">
                            <div className="bg-red-50 p-4 rounded-full mb-4">
                                <AlertTriangle className="w-8 h-8 text-red-500" />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {confirmModal.title}
                            </h3>
                            <p className="text-gray-600 text-sm leading-relaxed mb-8">
                                {confirmModal.message}
                            </p>
                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                                    className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmModal.onConfirm}
                                    className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg shadow-red-200 transition-all duration-200 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="flex justify-between items-center mb-6">
                <div />
                <div className="flex gap-2">
                    <button
                        onClick={() => setShowInstructions(true)}
                        className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                    >
                        <Info className="w-4 h-4" />
                        How it works?
                    </button>
                    <button
                        onClick={handleExport}
                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                    >
                        <Download className="w-4 h-4" />
                        Export Data
                    </button>
                    <label className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg cursor-pointer transition-colors text-sm font-medium">
                        <Upload className="w-4 h-4" />
                        Bulk Upload
                        <input type="file" className="hidden" accept=".xlsx" onChange={handleBulkUpload} />
                    </label>
                    <button
                        onClick={() => { resetForm(); setShowCreateDialog(true); }}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium"
                    >
                        <Plus className="w-4 h-4" />
                        Add Food Macro
                    </button>
                </div>
            </div>

            <style>
                {`
                  @media print {
                    body * { visibility: hidden; }
                    .print-section, .print-section * { visibility: visible; }
                    .print-section { 
                      position: absolute; 
                      left: 0; 
                      top: 0; 
                      width: 100%; 
                      padding: 40px;
                      background: white;
                    }
                    .no-print { display: none !important; }
                  }
                `}
            </style>

            {/* Instructions Modal */}
            {showInstructions && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4 backdrop-blur-sm overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl p-6 border border-gray-100 max-h-[90vh] overflow-y-auto print-section">
                        <div className="flex justify-between items-center mb-6 no-print">
                            <div className="flex items-center gap-3">
                                <div className="bg-indigo-100 p-2 rounded-lg">
                                    <Info className="w-6 h-6 text-indigo-600" />
                                </div>
                                <h2 className="text-xl font-bold text-gray-900">Bulk Upload Instructions</h2>
                            </div>
                            <button
                                onClick={() => setShowInstructions(false)}
                                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-lg transition-all"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        <div className="hidden print:block mb-8 text-center border-b pb-4">
                            <h1 className="text-3xl font-bold text-indigo-700">Food Macro Bulk Upload Guide</h1>
                            <p className="text-gray-500 mt-2">Administrative Control Panel - BYB Cult</p>
                        </div>

                        <div className="space-y-6 text-sm text-gray-700">
                            <section className="bg-blue-50 p-4 rounded-xl border border-blue-100">
                                <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                                    <Activity className="w-4 h-4" />
                                    Important Guidelines
                                </h3>
                                <ul className="space-y-2 list-disc pl-4 text-blue-800">
                                    <li><strong>Automatic Normalization:</strong> All "standard" unit entries (g, ml) in the Excel file are automatically normalized to 100g/100ml in the database.</li>
                                    <li><strong>Accuracy:</strong> Ensure macro values correspond to the quantity entered.</li>
                                    <li><strong>Unit Format:</strong> Use explicit units like 'g', 'kg', 'ml', 'l'.</li>
                                </ul>
                            </section>

                            <div className="overflow-x-auto">
                                <table className="w-full border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                            <th className="px-3 py-2 text-left font-bold text-gray-600">Column Name</th>
                                            <th className="px-3 py-2 text-left font-bold text-gray-600">Description / Example</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {[
                                            { col: 'food', val: 'Food Name (e.g., Chicken Breast)' },
                                            { col: 'qty', val: 'Quantity (e.g., 100)' },
                                            { col: 'unit', val: "Unit (e.g., 'g', 'kg', 'ml', 'pcs')" },
                                            { col: 'caloriesInKcal', val: 'Calories (kcal)' },
                                            { col: 'carbs / protein / fat', val: 'Macros in grams' },
                                            { col: 'remarks', val: 'Optional (e.g., Boiled, Raw)' },
                                        ].map((row, i) => (
                                            <tr key={i} className="border-b border-gray-50">
                                                <td className="px-3 py-3 font-semibold text-indigo-700 align-top w-1/3">{row.col}</td>
                                                <td className="px-3 py-3">{row.val}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="flex gap-4 no-print">
                                <button
                                    onClick={handleDownloadTemplate}
                                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-50 text-emerald-700 rounded-xl hover:bg-emerald-100 transition-colors font-bold border border-emerald-200"
                                >
                                    <Download className="w-5 h-5" />
                                    CSV Template
                                </button>
                                <button
                                    onClick={() => window.print()}
                                    className="flex-1 flex items-center justify-center gap-2 py-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-all font-bold shadow-lg shadow-indigo-100"
                                >
                                    <Printer className="w-5 h-5" />
                                    Save as PDF / Print
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Grid Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 flex items-center hover:shadow-md transition-all duration-300 group">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300">
                        <Utensils className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Total Items</p>
                        <p className="text-2xl font-black text-gray-900">{totalElements}</p>
                    </div>
                </div>
            </div>

            {/* Table Container */}
            <div className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
                <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50/50">
                    <div className="relative w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search by food name..."
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-sm transition-all text-black placeholder-gray-400"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => fetchMacros()}
                            className="p-2 hover:bg-white rounded-lg transition-colors border border-transparent hover:border-gray-200"
                        >
                            <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="p-12 text-center bg-white rounded-lg border border-gray-100">
                            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600 font-medium">Processing bulk upload...</p>
                            <p className="mt-1 text-sm text-gray-400">This may take a few moments depending on the file size.</p>
                        </div>
                    ) : (
                        <table className="w-full text-left">
                            <thead className="bg-gray-50 text-gray-500 text-xs font-semibold uppercase tracking-wider">
                                <tr>
                                    <th className="px-6 py-4">S.No</th>
                                    <th className="px-6 py-4 cursor-pointer group" onClick={() => handleSort('food')}>
                                        <div className="flex items-center">
                                            Food Item {renderSortIcon('food')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 cursor-pointer group" onClick={() => handleSort('qty')}>
                                        <div className="flex items-center">
                                            Portion {renderSortIcon('qty')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-orange-600 cursor-pointer group" onClick={() => handleSort('caloriesInKcal')}>
                                        <div className="flex items-center">
                                            Calories (kcal) {renderSortIcon('caloriesInKcal')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-green-600 cursor-pointer group" onClick={() => handleSort('proteinInGrams')}>
                                        <div className="flex items-center">
                                            Protein (g) {renderSortIcon('proteinInGrams')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-blue-600 cursor-pointer group" onClick={() => handleSort('carbsInGrams')}>
                                        <div className="flex items-center">
                                            Carbs (g) {renderSortIcon('carbsInGrams')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4 text-yellow-600 cursor-pointer group" onClick={() => handleSort('fatInGrams')}>
                                        <div className="flex items-center">
                                            Fat (g) {renderSortIcon('fatInGrams')}
                                        </div>
                                    </th>
                                    <th className="px-6 py-4">Remarks</th>
                                    <th className="px-6 py-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {macros.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50/80 transition-colors">
                                        <td className="px-6 py-4 text-sm text-gray-400 font-mono">
                                            {(currentPage * pageSize) + macros.indexOf(item) + 1}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="font-bold text-gray-900">{item.food || '-'}</div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 font-medium">
                                            {item.qty && item.unit ? `${item.qty} ${item.unit}` : '-'}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-bold text-orange-600">{item.caloriesInKcal != null ? `${item.caloriesInKcal} kcal` : '-'}</td>
                                        <td className="px-6 py-4 text-sm font-bold text-green-600">{item.proteinInGrams != null ? `${item.proteinInGrams}g` : '-'}</td>
                                        <td className="px-6 py-4 text-sm font-bold text-blue-600">{item.carbsInGrams != null ? `${item.carbsInGrams}g` : '-'}</td>
                                        <td className="px-6 py-4 text-sm font-bold text-yellow-600">{item.fatInGrams != null ? `${item.fatInGrams}g` : '-'}</td>
                                        <td className="px-6 py-4 text-xs text-gray-500 italic max-w-xs truncate">{item.remarks || '-'}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() => openEditDialog(item)}
                                                    className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                >
                                                    <Edit className="w-4 h-4" />
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>


            </div>
            {/* Pagination */}
            <Pagination
                currentPage={currentPage + 1}
                setCurrentPage={(page) => fetchMacros(page - 1)}
                totalPages={totalPages}
                totalElements={totalElements}
                pageSize={pageSize}
                setPageSize={(size) => {
                    setPageSize(size);
                    fetchMacros(0, size);
                }}
                itemName="Food Macros"
            />

            {/* Modal Overlay */}
            {(showCreateDialog || showEditDialog) && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-200">
                        <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                            <h2 className="text-xl font-bold text-gray-900">{showEditDialog ? 'Edit Food Macro' : 'Add New Food Macro'}</h2>
                            <button onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); }} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                                <X className="w-5 h-5 text-gray-500" />
                            </button>
                        </div>

                        <div className="p-6 grid grid-cols-2 gap-4">
                            <div className="col-span-2 space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Food Name <span className="text-red-500">*</span></label>
                                <input
                                    type="text"
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.food ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400`}
                                    placeholder="e.g. Chicken Breast"
                                    value={formData.food}
                                    onChange={(e) => {
                                        setFormData({ ...formData, food: e.target.value });
                                        if (formErrors.food) setFormErrors({ ...formErrors, food: '' });
                                    }}
                                />
                                {formErrors.food && <p className="text-red-500 text-xs mt-1">{formErrors.food}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Quantity <span className="text-red-500">*</span></label>
                                <input
                                    type="number"
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.qty ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400`}
                                    placeholder="e.g. 100"
                                    value={formData.qty}
                                    onChange={(e) => {
                                        setFormData({ ...formData, qty: e.target.value });
                                        if (formErrors.qty) setFormErrors({ ...formErrors, qty: '' });
                                    }}
                                />
                                {formErrors.qty && <p className="text-red-500 text-xs mt-1">{formErrors.qty}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Unit <span className="text-red-500">*</span></label>
                                <select
                                    className={`w-full px-4 py-2.5 rounded-xl border ${formErrors.unit ? 'border-red-500' : 'border-gray-200'} focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black`}
                                    value={formData.unit}
                                    onChange={(e) => {
                                        setFormData({ ...formData, unit: e.target.value });
                                        if (formErrors.unit) setFormErrors({ ...formErrors, unit: '' });
                                    }}
                                >
                                    <option value="g">Grams (g)</option>
                                    <option value="ml">Milliliters (ml)</option>
                                    <option value="pcs">Pieces (pcs)</option>
                                    <option value="scoop">Scoop (g)</option>
                                </select>
                                {formErrors.unit && <p className="text-red-500 text-xs mt-1">{formErrors.unit}</p>}
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider text-orange-600">Calories (kcal)</label>
                                <input
                                    type="number"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400"
                                    placeholder="e.g. 165"
                                    value={formData.caloriesInKcal}
                                    onChange={(e) => setFormData({ ...formData, caloriesInKcal: e.target.value })}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider text-green-600">Protein (g)</label>
                                <input
                                    type="number"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400"
                                    placeholder="e.g. 31"
                                    value={formData.proteinInGrams}
                                    onChange={(e) => setFormData({ ...formData, proteinInGrams: e.target.value })}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider text-blue-600">Carbs (g)</label>
                                <input
                                    type="number"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400"
                                    placeholder="e.g. 0"
                                    value={formData.carbsInGrams}
                                    onChange={(e) => setFormData({ ...formData, carbsInGrams: e.target.value })}
                                />
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider text-yellow-600">Fat (g)</label>
                                <input
                                    type="number"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400"
                                    placeholder="e.g. 3.6"
                                    value={formData.fatInGrams}
                                    onChange={(e) => setFormData({ ...formData, fatInGrams: e.target.value })}
                                />
                            </div>

                            <div className="col-span-2 space-y-1">
                                <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">Remarks</label>
                                <textarea
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 outline-none text-sm transition-all text-black placeholder-gray-400 min-h-[80px]"
                                    placeholder="Optional notes or brand names..."
                                    value={formData.remarks}
                                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                />
                            </div>
                        </div>

                        <div className="p-6 bg-gray-50 flex justify-end gap-3 border-t border-gray-100">
                            <button
                                onClick={() => { setShowCreateDialog(false); setShowEditDialog(false); }}
                                className="px-6 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-white transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={showEditDialog ? handleUpdate : handleCreate}
                                className="px-8 py-2.5 rounded-xl bg-blue-600 text-white font-bold text-sm hover:bg-blue-700 shadow-lg shadow-blue-500/30 transition-all flex items-center gap-2"
                            >
                                <Check className="w-4 h-4" />
                                {showEditDialog ? 'Update Food' : 'Save Food Item'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Toast Notification */}
            {toast.show && (
                <div className={`fixed bottom-8 right-8 px-6 py-4 rounded-2xl shadow-2xl z-[100] flex items-center gap-3 animate-in fade-in slide-in-from-bottom-5 duration-300 ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                    }`}>
                    {toast.type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
                    <span className="font-bold">{toast.message}</span>
                </div>
            )}
        </div>
    );
}
